import { isMobileViewport } from '@globals/utils/functions';
import { InteractionStrategy, MenuCallbacks } from './InteractionStrategy';
import TimerManager from '../utils/TimerManager';
import DropdownAnimator from '../utils/DropdownAnimator';

class HoverStrategy implements InteractionStrategy {
	private menuItems: NodeListOf<Element> | null = null;
	private callbacks: MenuCallbacks | null = null;
	private timerManager: TimerManager = new TimerManager();
	private breakpoints: IBreakpoints | undefined;
	private eventListeners: Map<Element, Array<{ event: string; handler: EventListener }>> =
		new Map();
	private dropdownAnimator: DropdownAnimator | null = null;

	constructor(breakpoints: IBreakpoints | undefined) {
		this.breakpoints = breakpoints;
	}

	init(menuItems: NodeListOf<Element>, callbacks: MenuCallbacks): void {
		this.menuItems = menuItems;
		this.callbacks = callbacks;

		const isMobile = this.breakpoints && isMobileViewport(this.breakpoints);
		if (!isMobile) {
			const navElement = document.querySelector<HTMLElement>('.header-nav-menu');
			if (navElement) {
				this.dropdownAnimator = new DropdownAnimator(navElement);
			}
		}

		menuItems.forEach((item) => {
			const listeners: Array<{ event: string; handler: EventListener }> = [];

			const mouseEnterHandler = this.handleMouseEnter.bind(this) as EventListener;
			const mouseLeaveHandler = this.handleMouseLeave.bind(this) as EventListener;
			item.addEventListener('mouseenter', mouseEnterHandler);
			item.addEventListener('mouseleave', mouseLeaveHandler);
			listeners.push({ event: 'mouseenter', handler: mouseEnterHandler });
			listeners.push({ event: 'mouseleave', handler: mouseLeaveHandler });

			this.eventListeners.set(item, listeners);
		});

		const megaMenuLinks = Array.from(menuItems).flatMap((item) =>
			Array.from(
				item.querySelectorAll<HTMLAnchorElement>(
					'.header-nav-menu__nav-item > a, .mm-dropdown-items__item > a, .mm-dropdown-items__nav-link'
				)
			)
		);

		megaMenuLinks.forEach((link) => {
			const focusInHandler = this.handleMouseEnter.bind(this) as EventListener;
			const focusOutHandler = ((e: Event) => {
				const focusEvent = e as FocusEvent;
				const parentItem = link.closest(
					'.header-nav-menu__nav-item--has-dropdown, .header-nav-menu__nav-item--has-mm-dropdown'
				);

				if (parentItem) {
					if (!parentItem.contains(focusEvent.relatedTarget as HTMLElement)) {
						this.handleMouseLeave(e as unknown as MouseEvent);
					}
				}
			}) as EventListener;

			link.addEventListener('focusin', focusInHandler);
			link.addEventListener('focusout', focusOutHandler);

			const linkListeners = this.eventListeners.get(link) || [];
			linkListeners.push({ event: 'focusin', handler: focusInHandler });
			linkListeners.push({ event: 'focusout', handler: focusOutHandler });
			this.eventListeners.set(link, linkListeners);
		});
	}

	private handleMouseEnter(event: MouseEvent): void {
		if (this.breakpoints && isMobileViewport(this.breakpoints)) return;

		let target = event.currentTarget as HTMLElement;

		if (
			event.type === 'focusin' &&
			target.tagName === 'A' &&
			(target.parentElement?.classList.contains('header-nav-menu__nav-item--has-dropdown') ||
				target.parentElement?.classList.contains(
					'header-nav-menu__nav-item--has-mm-dropdown'
				))
		) {
			target = target.parentElement;
		}

		if (!target.classList.contains('header-nav-menu__nav-item')) return;

		const isStandardDropdown = target.classList.contains(
			'header-nav-menu__nav-item--has-dropdown'
		);

		if (isStandardDropdown && this.dropdownAnimator) {
			this.dropdownAnimator.cancelClose();

			if (this.dropdownAnimator.getActiveItem() === target) return;

			document.body.classList.add('dropdown-open');
			this.dropdownAnimator.open(target);
			return;
		}

		const isMegaMenu = target.classList.contains('header-nav-menu__nav-item--has-mm-dropdown');
		if (isMegaMenu) {
			this.timerManager.clear(target);

			const isAlreadyOpen = !!target.querySelector<HTMLElement>(
				'.header-nav-menu__nav-link.is-open'
			);
			if (isAlreadyOpen) return;

			if (this.dropdownAnimator) {
				this.dropdownAnimator.forceClose();
			}

			this.timerManager.set(
				target,
				() => {
					document.body.classList.add('dropdown-open');
					if (this.callbacks) {
						this.callbacks.onOpen(target);
					}
				},
				200
			);
		}
	}

	private handleMouseLeave(event: MouseEvent): void {
		if (this.breakpoints && isMobileViewport(this.breakpoints)) return;

		let target = event.currentTarget as HTMLElement;
		if (event.type === 'focusout' && target.tagName === 'A') {
			const ancestor = target.closest<HTMLElement>(
				'.header-nav-menu__nav-item--has-dropdown, .header-nav-menu__nav-item--has-mm-dropdown'
			);
			if (ancestor) {
				target = ancestor;
			}
		}

		if (!target.classList.contains('header-nav-menu__nav-item')) return;

		const isStandardDropdown = target.classList.contains(
			'header-nav-menu__nav-item--has-dropdown'
		);
		const isMegaMenu = target.classList.contains('header-nav-menu__nav-item--has-mm-dropdown');

		if (isStandardDropdown && this.dropdownAnimator) {
			this.dropdownAnimator.close();
		} else if (isMegaMenu) {
			this.timerManager.set(
				target,
				() => {
					document.body.classList.remove('dropdown-open');
					if (this.callbacks) {
						this.callbacks.onClose(target);
					}
				},
				200
			);
		}
	}

	destroy(): void {
		this.timerManager.clearAll();

		this.eventListeners.forEach((listeners, element) => {
			listeners.forEach(({ event, handler }) => {
				element.removeEventListener(event, handler);
			});
		});

		this.eventListeners.clear();

		if (this.dropdownAnimator) {
			this.dropdownAnimator.destroy();
			this.dropdownAnimator = null;
		}
	}
}

export default HoverStrategy;
